import dayjs from "dayjs";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheConfig {
  defaultTtl: number; // Default TTL in milliseconds (5 seconds = 5,000 ms)
}

class HomePageCacheService {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private config: CacheConfig;

  constructor(config: CacheConfig = { defaultTtl: 60 * 15 * 1000 }) {
    this.config = config;
  }

  /**
   * Get data from cache if it exists and hasn't expired
   * @param key - Cache key
   * @returns Cached data or null if not found/expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - entry.timestamp > entry.ttl;

    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set data in cache with specified TTL
   * @param key - Cache key
   * @param data - Data to cache
   * @param ttl - Time to live in milliseconds (optional, uses default if not provided)
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const cacheEntry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTtl,
    };

    this.cache.set(key, cacheEntry);
  }

  /**
   * Check if data exists in cache and hasn't expired
   * @param key - Cache key
   * @returns true if data exists and is valid
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);

    if (!entry) {
      return false;
    }

    const now = Date.now();
    const isExpired = now - entry.timestamp > entry.ttl;

    if (isExpired) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Delete specific cache entry
   * @param key - Cache key to delete
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   * @returns Object with cache stats
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Get remaining time for a cache entry
   * @param key - Cache key
   * @returns Remaining time in milliseconds, or null if key doesn't exist
   */
  getRemainingTime(key: string): number | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    const now = Date.now();
    const elapsed = now - entry.timestamp;
    const remaining = entry.ttl - elapsed;

    return Math.max(0, remaining);
  }
}

// Create a global cache instance that persists across module reloads
declare global {
  var __homePageCacheInstance: HomePageCacheService | undefined;
}

// Export a singleton instance with 30-second default TTL
export const homePageCacheService = globalThis.__homePageCacheInstance || new HomePageCacheService({ defaultTtl: 60 * 15 * 1000 });
globalThis.__homePageCacheInstance = homePageCacheService;

// Export cache keys as constants for consistency
export const CACHE_KEYS = {
  BANNER_DATA: 'banner_data',
  SKU_STOCK_DATA: 'sku_stock_data',
  PENDING_ORDERS_DATA: 'pending_orders_data',
  PRODUCT_DATA: 'product_data',
} as const;

// Export utility functions for common operations
export const getCachedData = <T>(key: string): T | null => {
  return homePageCacheService.get<T>(key);
};

export const setCachedData = <T>(key: string, data: T, ttl?: number): void => {
  homePageCacheService.set<T>(key, data, ttl);
  // Track last updated time for dashboard data
  if (key.includes('banner_data') || key.includes('pendingOrders') || key.includes('skustockinfo') || key.includes('collection')) {
    localStorage.setItem('homePageLastUpdated', Date.now().toString());
  }
};

export const hasCachedData = (key: string): boolean => {
  return homePageCacheService.has(key);
};

export const clearCache = (key?: string): void => {
  if (key) {
    homePageCacheService.delete(key);
  } else {
    homePageCacheService.clear();
  }
};

export const getLastUpdatedTime = (): Date | null => {
  const timestamp = localStorage.getItem('homePageLastUpdated');
  return timestamp ? new Date(parseInt(timestamp)) : null;
};

export const formatLastUpdatedTime = (): string => {
  const lastUpdated = getLastUpdatedTime();
  if (!lastUpdated) return '';
  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - lastUpdated.getTime()) / (1000 * 60));
  
  if (diffMinutes === 0) return 'Just now';
  if (diffMinutes === 1) return '1 min ago';
  if (diffMinutes < 60) return `${diffMinutes} mins ago`;
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  
  return lastUpdated.toLocaleDateString();
};

export const formatLastUpdatedAbsolute = (): string => {
  const lastUpdated = getLastUpdatedTime();
  return lastUpdated ? dayjs(lastUpdated).format("YYYY-MM-DD HH:mm:ss") : "";
};



